/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Flex, Section, Container } from '@tishman/components';
import { graphql, useStaticQuery } from 'gatsby';
import React, { useMemo } from 'react';

import { HolidayCrossLink } from '../components/Holidays/HolidayCrossLink';

import type { ComponentPropsWithoutRef } from 'react';

const HOLIDAYS_CROSS_LINK_QUERY = graphql`
  query HolidaysCrossLink {
    sanityAttractionHoliday(
      _id: { eq: "theHolidays" }
      _type: { eq: "attraction.holiday" }
    ) {
      twoColumnCard {
        title
        image {
          asset {
            gatsbyImageData(width: 700, layout: CONSTRAINED)
          }
          alt
          caption
        }
        link {
          label
          url
        }
      }
    }
  }
`;

const HolidaysCrossLinkBlock = (
  props: ComponentPropsWithoutRef<typeof Section>,
): React.JSX.Element | null => {
  const { sanityAttractionHoliday } =
    useStaticQuery<Queries.HolidaysCrossLinkQuery>(HOLIDAYS_CROSS_LINK_QUERY);
  const holidaysCrossLinkProps = useMemo(() => {
    return (
      sanityAttractionHoliday?.twoColumnCard?.map((columnCard) => {
        if (!columnCard) throw new Error('expected valid columnCard');
        if (!columnCard.image)
          throw new Error('expected valid columnCard.image');
        if (!columnCard.image.alt)
          throw new Error('expected valid columnCard.image.alt');
        if (!columnCard.image.asset)
          throw new Error('expected valid columnCard.image.asset');
        if (!columnCard.image.asset.gatsbyImageData)
          throw new Error(
            'expected valid columnCard.image.asset.gatsbyImageData',
          );
        if (!columnCard.image.caption)
          throw new Error('expected valid columnCard.image.caption');
        if (!columnCard.link) throw new Error('expected valid columnCard.link');
        if (!columnCard.link.label)
          throw new Error('expected valid columnCard.link.label');
        if (!columnCard.link.url)
          throw new Error('expected valid columnCard.link.url');
        if (!columnCard.title)
          throw new Error('expected valid crossLink.title');

        return {
          gatsbyImageData: columnCard.image.asset.gatsbyImageData,
          alt: columnCard.image.alt,
          caption: columnCard.image.caption,
          title: columnCard.title,
          link: {
            label: columnCard.link.label,
            url: columnCard.link.url,
          },
        };
      }) ?? []
    );
  }, [sanityAttractionHoliday]);

  return holidaysCrossLinkProps.length > 0 ? (
    <Section data-section="HolidaysCrossLink" {...props}>
      <Container sx={{ py: [3, 4, 7] }}>
        <Flex
          sx={{
            justifyContent: 'space-between',
            flexDirection: ['column', null, 'row'],
          }}
        >
          {holidaysCrossLinkProps.map((crossLink) => (
            <HolidayCrossLink key={crossLink.title} {...crossLink} />
          ))}
        </Flex>
      </Container>
    </Section>
  ) : null;
};

export default HolidaysCrossLinkBlock;
